import React, { useEffect, useState } from 'react';
import { Candidate } from '../../types';
import Button from '../Form/Button';
import { resolveFileUrl } from '../../utils/fileUrl';
import { CandidateEvaluationTab } from './CandidateDetailsSections';
import SeafarerCandidateForm from './SeafarerCandidateForm';
import { contentManagerService } from '../../services/content-manager.service';
import { reverseTransformFieldNames } from '../../services/admin-resource-helpers';
import { API_CONFIG } from '../../config/api.config';

interface CandidateDetailsModalProps {
  candidate: Candidate;
  onClose: () => void;
  onUpdate: (candidate: Candidate) => void;
}

const statusOptions = [
  { value: 'new', label: 'New Application' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'new': return 'bg-gray-100 text-gray-800';
    case 'under_review': return 'bg-yellow-100 text-yellow-800';
    case 'interview_scheduled': return 'bg-blue-100 text-blue-800';
    case 'interviewed': return 'bg-indigo-100 text-indigo-800';
    case 'hired': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const CandidateDetailsModal: React.FC<CandidateDetailsModalProps> = ({ 
  candidate, 
  onClose, 
  onUpdate 
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'evaluation'>('details');
  // The list payload can omit the big seafarerData json — refetch the full
  // record via the content manager so the form renders every submitted field.
  const [fullCandidate, setFullCandidate] = useState<Candidate>(candidate);

  useEffect(() => {
    setFullCandidate(candidate);
    const documentId = (candidate as any).documentId;
    if (!documentId) return;
    let cancelled = false;
    contentManagerService
      .getById<any>(API_CONFIG.CONTENT_MANAGER_ENDPOINTS.CANDIDATES, documentId)
      .then((res) => {
        if (cancelled) return;
        const full = res?.data ?? res;
        if (!full || !full.documentId) return;
        const mapped = reverseTransformFieldNames(full, API_CONFIG.CONTENT_MANAGER_ENDPOINTS.CANDIDATES);
        setFullCandidate({ ...candidate, ...mapped });
      })
      .catch(() => { /* keep the list record as-is */ });
    return () => { cancelled = true; };
  }, [candidate]);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {candidate.firstName} {candidate.lastName}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Applied for {candidate.appliedPosition} • {new Date(candidate.appliedDate).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(candidate.status)}`}>
              {candidate.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Candidate Details
            </button>
            <button
              onClick={() => setActiveTab('evaluation')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'evaluation'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Evaluation & Feedback
            </button>
          </nav>
        </div>

        {/* Tab Content — the details view is the same seafarer form the
            website shows, rendered read-only with the submitted values. */}
        {activeTab === 'details' && (
          <SeafarerCandidateForm candidate={fullCandidate} readOnly />
        )}

        {activeTab === 'evaluation' && (
          <CandidateEvaluationTab
            candidate={candidate}
            onUpdate={onUpdate}
            statusOptions={statusOptions}
          />
        )}

        {/* Footer */}
        <div className="mt-8 flex justify-end space-x-3 pt-6 border-t border-gray-200">
          {candidate.resumeUrl && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.open(resolveFileUrl(candidate.resumeUrl), '_blank')}
            >
              View Resume
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CandidateDetailsModal;
